"use client";

import { useState, useEffect } from "react";
import axios from "axios";
import ConfirmationDialog from "../components/ConfirmationDialog";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const VendorManagement = () => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [newVendor, setNewVendor] = useState({
    full_name: "",
    email: "",
    phone_number: "",
    password: "",
  });

  const [vendorToDelete, setVendorToDelete] = useState(null);

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `https://logicx.premierlam.com/api/pixel-get-all-vendor`,
        {
          headers: {
            authorization: localStorage.getItem("accessToken"),
          },
        }
      );
      setVendors(response.data.data || []);
    } catch (err) {
      setError("Failed to fetch vendor list");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItems(vendors.map((v) => v.id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleAddVendor = async (e) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      await axios.post(
        `https://logicx.premierlam.com/api/pixel-add-vendor`,
        newVendor,
        {
          headers: {
            authorization: localStorage.getItem("accessToken"),
            "Content-Type": "application/json",
          },
        }
      );
      await fetchVendors();
      setIsAddDialogOpen(false);
      setNewVendor({ full_name: "", email: "", phone_number: "", password: "" });
      toast.success("Vendor added successfully!");
    } catch (err) {
      toast.error(err.response?.data?.mainerror || "Failed to add vendor");
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredVendors = vendors.filter((v) =>
    v.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />


      <div className="min-h-screen bg-gray-50 p-2">
        <div className="max-w-7xl mx-auto">
          {/* HEADER */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 mb-1">Vendors</h1>
              <p className="text-gray-500">Manage vendor list</p>
            </div>
            <button
              onClick={() => setIsAddDialogOpen(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Vendor
            </button>
          </div>

          {/* TABLE CONTAINER */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">

            {/* SEARCH */}
            <div className="relative mb-6">
              <svg className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search vendor"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 w-64"
              />
            </div>

            {/* TABLE */}
            <div className="overflow-x-auto">
              {isLoading ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
                </div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="py-3 px-4 w-12">
                        <input
                          type="checkbox"
                          checked={selectedItems.length === vendors.length}
                          onChange={handleSelectAll}
                        />
                      </th>
                      <th className="py-3 px-4 text-sm font-medium text-gray-600">Vendor Name</th>
                      <th className="py-3 px-4 text-sm font-medium text-gray-600">Email</th>
                      <th className="py-3 px-4 text-sm font-medium text-gray-600">Phone</th>
                      <th className="py-3 px-4 text-sm font-medium text-gray-600">Role</th>
                      <th className="py-3 px-4 text-sm font-medium text-gray-600">Status</th>
                      <th className="py-3 px-4 text-sm font-medium text-gray-600">Created On</th>
                      <th className="py-3 px-4 text-sm font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredVendors.map((v) => (
                      <tr key={v.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-4 px-4">
                          <input type="checkbox" checked={selectedItems.includes(v.id)} onChange={() => handleSelectItem(v.id)} />
                        </td>
                        <td className="py-4 px-4 text-sm text-gray-900">{v.full_name}</td>
                        <td className="py-4 px-4 text-sm text-gray-600">{v.email}</td>
                        <td className="py-4 px-4 text-sm text-gray-600">{v.phone_number}</td>
                        <td className="py-4 px-4 text-sm text-gray-600">{v.role}</td>
                        <td className="py-4 px-4 text-sm text-gray-600">{v.status}</td>
                        <td className="py-4 px-4 text-sm text-gray-600">{formatDate(v.created_at)}</td>

                        {/* ACTION ICONS DISABLED */}
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            <button disabled className="p-1 text-gray-300 cursor-not-allowed">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>

                            <button disabled className="p-1 text-gray-300 cursor-not-allowed">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m-4-6V4a1 1 0 011-1h4a1 1 0 011 1v3" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

          </div>

          {/* ADD DIALOG */}
          {isAddDialogOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
              <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative">
                <button className="absolute top-2 right-2 text-gray-400 hover:text-gray-700" onClick={() => setIsAddDialogOpen(false)}>
                  ✖
                </button>

                <h2 className="text-lg font-semibold mb-4">Add New Vendor</h2>

                <form onSubmit={handleAddVendor} className="space-y-4">
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={newVendor.full_name}
                    onChange={(e) => setNewVendor({ ...newVendor, full_name: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    required
                  />

                  <input
                    type="email"
                    placeholder="Email"
                    value={newVendor.email}
                    onChange={(e) => setNewVendor({ ...newVendor, email: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    required
                  />

                  <input
                    type="text"
                    placeholder="Phone Number"
                    value={newVendor.phone_number}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d{0,10}$/.test(value)) {   // allow only digits up to 10
                        setNewVendor({ ...newVendor, phone_number: value });
                      }
                    }}
                    onBlur={() => {
                      if (newVendor.phone_number.length !== 10) {
                        alert("Mobile number must be 10 digits");
                      }
                    }}
                    className="w-full border rounded px-3 py-2"
                    required
                  />


                  <input
                    type="text"
                    placeholder="Password"
                    value={newVendor.password}
                    onChange={(e) => setNewVendor({ ...newVendor, password: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    required
                  />

                  <div className="flex justify-end gap-3">
                    <button type="button" className="px-4 py-2 bg-gray-100 rounded-lg" onClick={() => setIsAddDialogOpen(false)}>Cancel</button>
                    <button type="submit" disabled={isSubmitting}
                      className="px-4 py-2 bg-orange-500 text-white rounded-lg">
                      {isSubmitting ? "Adding..." : "Add Vendor"}
                    </button>
                  </div>

                </form>
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default VendorManagement;

